import { Link, useLocation, Outlet, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BarChart3, Music2, Disc, Video, Image, Home, ShoppingCart, Settings, LogOut, ArrowLeft, Menu, X, ShoppingBag, Sparkles, Crown, Users, Mail, Trophy, Target, Lock, TrendingUp } from "lucide-react";
import { useState } from "react";

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", to: "/admin" },
  { icon: TrendingUp, label: "Growth", to: "/admin/growth" },
  { icon: BarChart3, label: "Analytics", to: "/admin/analytics" },
  { icon: Music2, label: "Music", to: "/admin/music" },
  { icon: Disc, label: "Albums / EPs", to: "/admin/albums" },
  { icon: Video, label: "Videos", to: "/admin/videos" },
  { icon: Image, label: "Images", to: "/admin/images" },
  { icon: Home, label: "Homepage", to: "/admin/homepage" },
  { icon: ShoppingCart, label: "Store", to: "/admin/store" },
  { icon: Sparkles, label: "Affiliate", to: "/admin/affiliate" },
  { icon: ShoppingBag, label: "Orders", to: "/admin/orders" },
  { icon: Crown, label: "Tiers", to: "/admin/tiers" },
  { icon: Users, label: "Members", to: "/admin/members" },
  { icon: Mail, label: "Campaigns", to: "/admin/campaigns" },
  { icon: Trophy, label: "Rewards", to: "/admin/rewards" },
  { icon: Target, label: "Missions", to: "/admin/missions" },
  { icon: Lock, label: "Vault", to: "/admin/vault" },
  { icon: Settings, label: "Settings", to: "/admin/settings" },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar overlay on mobile */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-[#0A0A0A] border-r border-white/5 flex flex-col transition-transform duration-300 ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-white/5 flex items-center justify-between">
          <Link to="/admin" className="font-heading text-xl text-[#D4AF37] tracking-wider">LUXIFER 6</Link>
          <button className="lg:hidden text-white/40" onClick={() => setSidebarOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <p className="px-5 pt-3 text-[10px] uppercase tracking-[0.3em] text-white/25">Admin Panel</p>

        <nav className="flex-1 p-3 space-y-1 mt-2">
          {navItems.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded text-sm transition-colors ${active ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-white/50 hover:text-white hover:bg-white/5"}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/5 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded text-sm text-white/40 hover:text-white hover:bg-white/5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> View Site
          </Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-3 px-3 py-2.5 rounded text-sm text-white/40 hover:text-red-400 hover:bg-red-400/5 transition-colors w-full">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="h-14 border-b border-white/5 flex items-center justify-between px-6 bg-[#0A0A0A]/50 backdrop-blur-sm sticky top-0 z-30">
          <button className="lg:hidden text-white/60" onClick={() => setSidebarOpen(true)} data-testid="admin-sidebar-toggle">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden lg:block">
            {location.pathname !== "/admin" && (
              <Link to="/admin" data-testid="back-to-dashboard-btn" className="flex items-center gap-2 text-xs text-white/40 hover:text-[#D4AF37] transition-colors uppercase tracking-wider">
                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
              </Link>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-white/40">{user.email}</span>
            <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] text-xs font-bold">
              {user.name?.charAt(0) || "A"}
            </div>
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
